import {
  afterNextRender,
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { decodeHTML } from 'entities';

import { GoogleDataLayerService, MetaTagsService } from '../../services';

import { ContentBlockComponent } from '../../shared-components/content-block/content-block.component';
import { BenefitCardComponent } from '../../shared-components/benefit-card/benefit-card.component';
import { MembershipTableComponent } from '../../shared-components/membership-table/membership-table.component';
import { HeroSearchComponent } from './hero-search/hero-search.component';

import { ProgramType } from '../../models/program.model';
import { ChipItem } from '../../models/home-chip.model';
import { LandingPageBundleContent } from '../../models/landing-page.model';
import { BenefitCardItem } from '../../models/benefit-card.model';
import { ToggleOption } from 'src/app/uikit/toggle/toggle.component';
import { membershipBenefitsByProgramType, ProgramTypeOptions } from './home.data';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-home',
  imports: [
    HeroSearchComponent,
    ContentBlockComponent,
    BenefitCardComponent,
    MembershipTableComponent,
  ],
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  /** Placeholder count for the benefit-card skeleton grid. */
  protected readonly skeletonCardIndices = [0, 1, 2];

  /* Dependency injection */
  private readonly metaTagsService = inject(MetaTagsService);
  private readonly router = inject(Router);
  private readonly dataLayerService = inject(GoogleDataLayerService);
  private readonly apiService = inject(ApiService);

  /* Public state */
  public readonly programTypeOptions: ToggleOption<ProgramType>[] = ProgramTypeOptions;
  public readonly selectedProgramType = signal<ProgramType>(
    this.router.url === '/fellowship' ? 'Fellowship' : 'Residency',
  );
  /* Async data */
  public readonly homeData = toSignal(
    this.apiService.getPageData('home').pipe(
      tap((data) => {
        const pageData = data?.[0];
        if (!pageData) return;

        this.metaTagsService.setTitle(decodeHTML(pageData.PageTitle));
        this.metaTagsService.setDescription(decodeHTML(pageData.Description));
        this.metaTagsService.setCanonical(`https://freida.ama-assn.org${this.router.url}`);
      }),
      map((data) => data?.[0]?.Sections?.[0]?.content ?? []),
      catchError((err) => {
        console.error('Error fetching home data', err);
        return of([] as LandingPageBundleContent[]);
      }),
    ),
    { initialValue: null as LandingPageBundleContent[] | null },
  );

  /* Private derived state */
  private readonly activeSection = computed(() => {
    const all = this.homeData();
    if (!all) return null;
    const name = this.selectedProgramType();
    return all.find((c) => c.admin_title === name)?.content ?? [];
  });

  private byProgramType = (bundle: string): LandingPageBundleContent | undefined =>
    this.activeSection()?.find((c: LandingPageBundleContent) => c.bundle === bundle);

  private readonly chipBlock = computed(() => this.byProgramType('chip'));

  /** True while the home API data has not yet resolved (null = not yet loaded). */
  public readonly homeLoading = computed(() => this.homeData() === null);

  /* Public derived state (template-bound) */
  public readonly contentBlockData = computed<LandingPageBundleContent | undefined>(() => {
    const item = this.byProgramType('image_text');
    return item ? { ...item, body_copy: item.body_copy || '' } : undefined;
  });

  public readonly cards = computed<BenefitCardItem[]>(() =>
    (this.byProgramType('3_column')?.content ?? [])
      .filter((c) => c.bundle === 'benefit_card')
      .map((c) => ({
        title: c.title || '',
        description: c.body_copy || '',
        href: c.link || '',
        icon: c.icon?.name,
        iconType: c.icon?.type,
      })),
  );

  public readonly suggestedPrograms = computed<ChipItem[]>(() =>
    (this.chipBlock()?.chip_items ?? []).map((item: any) => ({
      id: item.chip_name?.id,
      label: item.chip_name?.title,
      icon: item.icon?.name,
      iconType: item.icon?.type,
      url: '/search/list',
      queryParams: { spec: item.chip_name?.id, fromHeader: 'true' },
    })),
  );

  public readonly suggestedProgramTitle = computed(() => this.chipBlock()?.title || '');

  public readonly membershipBenefits = computed(
    () => membershipBenefitsByProgramType[this.selectedProgramType()],
  );

  /* Constructor */
  constructor() {
    afterNextRender(() => {
      this.dataLayerService.trackAnalytics({
        site_events: {
          virtual_pageview: true,
          pwa_app_install: true,
        },
        virtual_uri: window.location.pathname,
        virtual_title: window.document.title,
      });
    });
  }

  /* Event handlers */
  public onProgramTypeChange(value: ProgramType): void {
    this.selectedProgramType.set(value);
    const targetUrl = value === 'Fellowship' ? '/fellowship' : '/';
    this.router.navigate([targetUrl], { replaceUrl: true });
    this.dataLayerService.trackAnalytics({
      site_events: {
        freida_home_toggle: true,
      },
      freida_home_program_type: value,
    });
  }
}
